'use client'
import React, { useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import RadarIcon from '@mui/icons-material/Radar'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'
import ConfigSlider from '@components/Form/ConfigSlider'
import { Tooltip } from '@mui/material'
import KeySelector from '@components/CustomInput/KeySelector'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import SpeedIcon from '@mui/icons-material/Speed'
import StarIcon from '@mui/icons-material/Star'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import MapIcon from '@mui/icons-material/Map'
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap'
import AspectRatioIcon from '@mui/icons-material/AspectRatio'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'

const Hud_rotateRadar = keyframes`
    0% { transform: scale(var(--radar-scale)) rotate(0deg); }
    50% { transform: scale(var(--radar-scale)) rotate(40deg); }
    100% { transform: scale(var(--radar-scale)) rotate(0deg); }
`

const Hud_dynamicZoom = keyframes`
    0%, 100% { transform: scale(var(--radar-scale)); }
    50% { transform: scale(calc(var(--radar-scale) * 1.25)); }
`

const Hud_rotateAndZoom = keyframes`
    0% { transform: scale(var(--radar-scale)) rotate(0deg); }
    50% { transform: scale(calc(var(--radar-scale) * 1.25)) rotate(20deg); }
    100% { transform: scale(var(--radar-scale)) rotate(0deg); }
`

const Hud_scanline = keyframes`
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100%); }
`

const Hud_Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    transition: opacity 0.2s ease;
    width: 100%;
    max-width: 920px;
`

const Hud_ColumnsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;
    align-items: flex-start;
    width: 100%;
`

const Hud_Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: ${({ isPreview }) => (isPreview ? '0 1 auto' : '1')};
    margin-top: 10px;
`

const Hud_PreviewBox = styled.div`
    background: rgba(15, 23, 42, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    height: 460px;
    min-width: 570px;
    width: 100%;
    max-width: 570px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    z-index: 105;
    box-shadow: inset 0px 0px 100px #05020879;

    background-image:
        linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
    background-size: 20px 20px;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, transparent, #6366f1, transparent);
    }

    @media (max-width: 1800px) {
        max-width: 100%;
    }
`

const Hud_PreviewToolbar = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 56px;
    background: rgba(10, 15, 28, 0.85);
    backdrop-filter: blur(12px);
    border-left: 1px solid rgba(99, 102, 241, 0.2);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 15px 0;
    gap: 12px;
    z-index: 120;
    box-shadow: -5px 0 20px rgba(0, 0, 0, 0.4);

    &::before {
        content: 'VIEWER';
        position: absolute;
        bottom: 40px;
        transform: rotate(-90deg);
        font-size: 9px;
        font-weight: 900;
        color: rgba(255, 255, 255, 0.1);
        letter-spacing: 3px;
        white-space: nowrap;
        pointer-events: none;
    }
`

const Hud_ToggleButton = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.02)'};
    border: 1px solid
        ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.25)')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &:hover {
        background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'};
        color: white;
        border-color: rgba(99, 102, 241, 0.6);
        box-shadow: 0 0 10px rgba(99, 102, 241, 0.15);
    }

    &::after {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%);
        opacity: ${({ active }) => (active ? 1 : 0)};
        pointer-events: none;
    }

    svg {
        font-size: 20px;
        filter: ${({ active }) => active ? 'drop-shadow(0 0 5px rgba(99, 102, 241, 0.8))' : 'none'};
        z-index: 1;
    }
`

const Toolbar_Separator = styled.div`
    width: 24px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    margin: 5px 0;
`

const Hud_Scanline = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        to bottom,
        transparent 0%,
        rgba(99, 102, 241, 0.05) 50%,
        transparent 100%
    );
    height: 40px;
    pointer-events: none;
    z-index: 15;
    animation: ${Hud_scanline} 4s linear infinite;
`

const Hud_CornerMarker = styled.div`
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    z-index: 15;
    pointer-events: none;

    ${({ corner }) => corner === 'tl' && 'top: 10px; left: 10px; border-right: none; border-bottom: none;'}
    ${({ corner }) => corner === 'tr' && 'top: 10px; right: 10px; border-left: none; border-bottom: none;'}
    ${({ corner }) => corner === 'bl' && 'bottom: 10px; left: 10px; border-right: none; border-top: none;'}
    ${({ corner }) => corner === 'br' && 'bottom: 10px; right: 10px; border-left: none; border-top: none;'}
`

const Hud_RadarContainer = styled.div`
    width: 300px;
    height: 300px;
    border-radius: 50%;
    border: 4px solid rgba(255, 255, 255, 0.03);
    background: rgba(0, 0, 0, 0.4);
    overflow: hidden;
    transform: scale(${({ scale }) => scale});
    opacity: ${({ opacity }) => opacity ?? 0.85};

    position: absolute;
    top: 20px;
    left: 20px;
    transform-origin: top left;
    z-index: 10;
    
    box-shadow: 
        0 0 0 1px rgba(99, 102, 241, 0.2),
        0 0 40px rgba(0, 0, 0, 0.4),
        inset 0 0 20px rgba(0,0,0,0.8);

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 50%;
        z-index: 20;
        pointer-events: none;
    }
`

const Hud_RadarMap = styled.div`
    width: 100%;
    height: 100%;
    background-image: url('/radar.jpg');
    background-size: cover;
    background-position: center;
    --radar-scale: ${({ zoom }) => zoom * 4};
    transform: scale(var(--radar-scale));
    opacity: 0.8;
    filter: brightness(0.5) contrast(1.1) sepia(0.9) hue-rotate(213deg) saturate(6);
    
    animation: ${({ isDynamic, rotate }) => {
        if (isDynamic && rotate) return css`${Hud_rotateAndZoom} 10s ease-in-out infinite`;
        if (isDynamic) return css`${Hud_dynamicZoom} 6s ease-in-out infinite`;
        if (rotate) return css`${Hud_rotateRadar} 20s ease-in-out infinite`;
        return 'none';
    }};
`

const Hud_PlayerDot = styled.div`
    width: ${({ iconScale }) => 16 * (iconScale || 1)}px;
    height: ${({ iconScale }) => 16 * (iconScale || 1)}px;
    background: #00ff00;
    border-radius: 50%;
    position: absolute;
    top: ${({ isCentered }) => (isCentered ? '50%' : 'calc(50% + 30px)')};
    left: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 5px #00ff00;
    z-index: 12;
    transition: top 0.3s ease-out, width 0.2s, height 0.2s;
`

const Hud_TeammateDot = styled.div`
    width: ${({ iconScale }) => 16 * (iconScale || 1)}px;
    height: ${({ iconScale }) => 16 * (iconScale || 1)}px;
    background: ${({ color, showColors }) => (showColors >= 1 ? color : '#fff')};
    border-radius: 50%;
    position: absolute;
    top: ${({ top }) => top};
    left: ${({ left }) => left};
    transform: translate(-50%, -50%);
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.7);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${({ iconScale }) => 12 * (iconScale || 1)}px;
    font-weight: 900;
    color: rgba(0, 0, 0, 0.9);
    transition: all 0.2s ease;

    &::after {
        content: ${({ letter, showColors }) => (showColors === 2 ? `'${letter}'` : "''")};
    }
`

const Hud_EnemyDot = styled.div`
    width: ${({ iconScale }) => 12 * (iconScale || 1)}px;
    height: ${({ iconScale }) => 12 * (iconScale || 1)}px;
    background: #ff4b4b;
    border-radius: 50%;
    position: absolute;
    top: ${({ top }) => top};
    left: ${({ left }) => left};
    transform: translate(-50%, -50%);
    box-shadow: 0 0 8px rgba(255, 75, 75, 0.8), 0 0 2px rgba(0, 0, 0, 0.5);
    z-index: 11;
    border: 1px solid rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 150%;
        height: 150%;
        background: radial-gradient(circle, rgba(255, 75, 75, 0.4) 0%, transparent 70%);
        transform: translate(-50%, -50%);
        pointer-events: none;
    }
`

const Hud_AmmoBars = styled.div`
    display: flex;
    gap: 2px;
    align-items: flex-end;
    height: 18px;

    div {
        width: 4px;
        height: 100%;
        background-color: ${({ color }) => color};
        border-radius: 1px;
        box-shadow: 0 0 5px ${({ color }) => color}80;

        &:nth-child(2) {
            height: 90%;
        }
        &:nth-child(3) {
            height: 80%;
        }
    }
`

const Hud_HealthPlus = styled.div`
    font-size: 24px;
    font-weight: 900;
    color: ${({ color }) => color};
    text-shadow: 0 0 10px ${({ color }) => color}80;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2px;
`

const Hud_CornerContainer = styled.div`
    position: absolute;
    bottom: 20px;
    ${({ side }) => (side === 'left' ? 'left: 20px;' : 'right: 80px;')}
    transform: scale(${({ scale }) => scale});
    transform-origin: ${({ side }) =>
        side === 'left' ? 'bottom left' : 'bottom right'};
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 20;

    .icon {
        font-size: 24px;
        color: ${({ color }) => color};
    }

    .value {
        font-size: 28px;
        font-weight: 400;
        color: ${({ color }) => color};
        text-shadow: 0 0 10px rgba(0, 0, 0, 0.8);
        font-family: var(--font-roboto);
    }
`

const Hud_InventoryPreview = styled.div`
    position: absolute;
    right: 70px;
    bottom: 90px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    width: 40px;
    opacity: ${({ visible }) => (visible ? 0.8 : 0)};
    transform: translateY(${({ visible }) => (visible ? '0' : '-10px')}) scale(${({ scale }) => scale * 0.9});
    transform-origin: top right;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    z-index: 15;

    &::before {
        content: 'INVENTORY';
        font-size: 8px;
        color: rgba(255, 255, 255, 0.15);
        letter-spacing: 2px;
        margin-bottom: 5px;
        transform: rotate(-90deg);
        margin: 40px 0;
    }
`

const Hud_InventoryItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ color }) => color};
    font-size: 14px;
    font-weight: 800;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.8));
    position: relative;
`

const Hud_ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
`

const Hud_ColorGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    width: fit-content;
    gap: 10px;
    margin-top: 12px;
`

const Hud_ColorSwatch = styled.div`
    width: 24px;
    height: 24px;
    background-color: ${({ color }) => color};
    border-radius: 4px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    border: 1.5px solid ${({ active }) => (active ? 'white' : 'transparent')};
    opacity: ${({ active, disabled }) => (disabled ? (active ? 0.6 : 0.2) : (active ? 1 : 0.3))};
    transition: all 0.2s ease;
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        opacity: 1;
        transform: ${({ disabled }) => (disabled ? 'none' : 'scale(1.15)')};
        border-color: rgba(255, 255, 255, 0.6);
    }
`

const Hud_Label = styled.span`
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
`

const Hud_Checkbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid ${({ checked }) => (checked ? '#6366f1' : '#444')};
    background: ${({ checked }) => (checked ? '#6366f1' : 'transparent')};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    svg {
        font-size: 16px;
        color: #fff;
    }
`

const Hud_ColorLabelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`

const Hud_ColorLabelText = styled.span`
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
`

const Hud_BindsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
    margin-top: 10px;
`

const Hud_C4Text = styled(Hud_InventoryItem)`
    font-size: 15px;
    letter-spacing: 2px;
`

// --- PRESETS STYLED COMPONENTS ---
const Presets_Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
    max-width: 900px;
    margin-top: 44px;
`

const Presets_Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 15px;
    width: 100%;
`

const Presets_Header = styled.div`
    font-size: 0.75rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-left: 5px;
    display: flex;
    align-items: center;
    gap: 10px;
`

const Preset_Card = styled.div`
    position: relative;
    background: rgba(15, 23, 42, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    height: 180px;
    justify-content: center;

    &:hover {
        transform: translateY(-5px);
        background: rgba(15, 23, 42, 0.6);
        border-color: rgba(99, 102, 241, 0.5);
        box-shadow: 0 10px 30px -10px rgba(99, 102, 241, 0.3);
        
        .preset-icon {
            color: #6366f1;
            transform: scale(1.1);
        }
    }
`

const Preset_IconWrapper = styled.div`
    font-size: 2.5rem;
    color: rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Preset_Title = styled.div`
    font-size: 0.9rem;
    font-weight: 700;
    color: #fff;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

const Preset_Desc = styled.div`
    font-size: 0.75rem;
    color: rgba(255,255,255,0.4);
    text-align: center;
    line-height: 1.4;
`

const Toggle_Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`


// HUD Colors Map (CS2 CVARs)
const HUD_COLORS = (t) => [
    { id: 0, color: '#9e9e9e', label: t('HUD_COLOR_DEFAULT') },
    { id: 1, color: '#d1d1d1', label: t('HUD_COLOR_SOFT_WHITE') },
    { id: 2, color: '#ffffff', label: t('HUD_COLOR_BRIGHT_WHITE') },
    { id: 3, color: '#add8e6', label: t('HUD_COLOR_LIGHT_BLUE') },
    { id: 4, color: '#0041c2', label: t('HUD_COLOR_DARK_BLUE') },
    { id: 5, color: '#d32ce6', label: t('HUD_COLOR_PURPLE') },
    { id: 6, color: '#ff4b4b', label: t('HUD_COLOR_RED') },
    { id: 7, color: '#ff8f1c', label: t('HUD_COLOR_ORANGE') },
    { id: 8, color: '#ffd700', label: t('HUD_COLOR_YELLOW') },
    { id: 9, color: '#3fdc46', label: t('HUD_COLOR_GREEN') },
    { id: 10, color: '#00ffff', label: t('HUD_COLOR_CYAN') },
    { id: 11, color: '#ffb5c6', label: t('HUD_COLOR_PINK') },
    { id: 12, color: '#87ceeb', label: t('HUD_COLOR_CELESTE') },
    { id: 13, color: '#d5b996', label: t('HUD_COLOR_BEIGE') },
]

export default function HudModule({ id, initialExpanded }) {
    const { t } = useLanguage()
    const { activeModules, configData, updateConfig, configIsLocked } = useConfig()
    const isActive = activeModules.includes('hud')
    const hud = configData.hud || {}

    const handleChange = (key, val) => {
        updateConfig('hud', key, val)
    }

    const colors = HUD_COLORS(t)
    // Get current color hex
    const currentColor =
        colors.find((c) => c.id === (hud.cl_hud_color || 1))?.color ||
        '#ffffff'

    const RADAR_PRESETS = [
        {
            id: 'compact',
            title: t('RADAR_COMPACT_TITLE') || 'Compact',
            desc: t('RADAR_COMPACT_DESC') || 'Radar pequeño con visión amplia',
            values: {
                cl_hud_radar_scale: 0.9,
                cl_radar_scale: 0.35,
                cl_radar_icon_scale_min: 0.5,
                cl_radar_always_centered: false,
                cl_radar_scale_dynamic: false,
            },
            icon: <AspectRatioIcon style={{ fontSize: 'inherit' }} />
        },
        {
            id: 'balanced',
            title: t('RADAR_BALANCED_TITLE') || 'Balanced',
            desc: t('RADAR_BALANCED_DESC') || 'Equilibrio entre tamaño y visibilidad',
            values: {
                cl_hud_radar_scale: 1.05,
                cl_radar_scale: 0.25,
                cl_radar_icon_scale_min: 0.6,
                cl_radar_always_centered: true,
                cl_radar_scale_dynamic: true,
            },
            icon: <ViewQuiltIcon style={{ fontSize: 'inherit' }} />
        },
        {
            id: 'competitive',
            title: t('RADAR_COMPETITIVE_TITLE') || 'Competitive (Pro)',
            desc: t('RADAR_COMPETITIVE_DESC') || 'Estándar usado por profesionales',
            values: {
                cl_hud_radar_scale: 1.0,
                cl_radar_scale: 0.3,
                cl_radar_icon_scale_min: 0.6,
                cl_radar_scale_dynamic: false,
                cl_radar_always_centered: true,
            },
            icon: <StarIcon style={{ fontSize: 'inherit' }} />
        },
        {
            id: 'giant',
            title: t('RADAR_GIANT_TITLE') || 'Giant Vision',
            desc: t('RADAR_GIANT_DESC') || 'Máximo tamaño y zoom (Novice)',
            values: {
                cl_hud_radar_scale: 1.25,
                cl_radar_icon_scale_min: 0.6,
                cl_radar_scale_dynamic: false,
                cl_radar_scale: 0.3,
                cl_radar_always_centered: true,
            },
            icon: <ZoomInMapIcon style={{ fontSize: 'inherit' }} />
        }
    ]

    const handleApplyPreset = (values) => {
        if (configIsLocked) return

        // Apply preset values
        Object.keys(values).forEach(key => {
            updateConfig('hud', key, values[key])
        })
    }

    return (
        <ModuleContainer
            id={id}
            moduleKey="hud"
            title={t('HUD_RADAR_TITLE')}
            description={t('HUD_RADAR_DESC')}
            icon={RadarIcon}
            initialExpanded={initialExpanded}
        >
            <Hud_Controls isActive={isActive}>

                <Hud_ColumnsContainer>
                    {/* PREVIEW COLUMN */}
                    <Hud_Column isPreview>
                        <Hud_PreviewBox>
                            {/* Radar Preview */}
                            <Hud_RadarContainer
                                scale={hud.cl_hud_radar_scale || 1}
                            >
                                <Hud_Scanline />
                                <Hud_CornerMarker corner="tl" />
                                <Hud_CornerMarker corner="tr" />
                                <Hud_CornerMarker corner="bl" />
                                <Hud_CornerMarker corner="br" />

                                <Hud_RadarMap
                                    zoom={hud.cl_radar_scale || 0.7}
                                    rotate={hud.cl_radar_rotate}
                                    isDynamic={hud.cl_radar_scale_dynamic}
                                />
                                <Hud_PlayerDot
                                    isCentered={hud.cl_radar_always_centered}
                                    iconScale={hud.cl_radar_icon_scale_min || 0.6}
                                />
                                <Hud_TeammateDot
                                    top="35%"
                                    left="25%"
                                    color="#ffff00"
                                    letter="Y"
                                    iconScale={hud.cl_radar_icon_scale_min || 0.6}
                                    showColors={hud.cl_teammate_colors_show ?? 1}
                                />
                                <Hud_TeammateDot
                                    top="55%"
                                    left="75%"
                                    color="#ff00ff"
                                    letter="P"
                                    iconScale={hud.cl_radar_icon_scale_min || 0.6}
                                    showColors={hud.cl_teammate_colors_show ?? 1}
                                />
                                {/* Enemies (Red Dots) */}
                                <Hud_EnemyDot
                                    top="40%"
                                    left="60%"
                                    iconScale={hud.cl_radar_icon_scale_min || 0.6}
                                />
                                <Hud_EnemyDot
                                    top="65%"
                                    left="35%"
                                    iconScale={hud.cl_radar_icon_scale_min || 0.6}
                                />
                            </Hud_RadarContainer>

                            {/* HUD Elements: Separate corners to scale individually */}
                            <Hud_CornerContainer
                                side="left"
                                scale={hud.hud_scaling || 0.85}
                                color={currentColor}
                            >
                                <Hud_HealthPlus color={currentColor}>+</Hud_HealthPlus>
                                <span className="value">100</span>
                            </Hud_CornerContainer>

                            <Hud_CornerContainer
                                side="right"
                                scale={hud.hud_scaling || 0.85}
                                color={currentColor}
                            >
                                <span className="value">30 / 90</span>
                                <Hud_AmmoBars color={currentColor}>
                                    <div />
                                    <div />
                                    <div />
                                </Hud_AmmoBars>
                            </Hud_CornerContainer>

                            {/* Inventory / Loadout Preview */}
                            <Hud_InventoryPreview
                                visible={hud.cl_showloadout}
                                scale={hud.hud_scaling || 0.85}
                            >
                                <Hud_C4Text color={currentColor}>C4</Hud_C4Text>
                                <Hud_InventoryItem color={currentColor}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <circle cx="12" cy="13" r="7" />
                                        <path d="M11 3h2v4h-2z" />
                                        <path d="M13 4c3 0 5 1 5 4l-2 1c0-2-1-3-3-3V4z" />
                                        <circle cx="12" cy="4" r="1.5" />
                                    </svg>
                                </Hud_InventoryItem>
                                <Hud_InventoryItem color={currentColor}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="6" cy="18" r="3" />
                                        <circle cx="18" cy="18" r="3" />
                                        <path d="M8.5 15.5L18 6M15.5 15.5L6 6" />
                                    </svg>
                                </Hud_InventoryItem>
                            </Hud_InventoryPreview>

                            <Hud_PreviewToolbar>
                                <Tooltip
                                    title={t('RADAR_CENTERED_LABEL')}
                                    placement="left"
                                >
                                    <Hud_ToggleButton
                                        active={hud.cl_radar_always_centered}
                                        onClick={() =>
                                            !configIsLocked && handleChange(
                                                'cl_radar_always_centered',
                                                !hud.cl_radar_always_centered
                                            )
                                        }
                                        disabled={configIsLocked}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="3" fill={hud.cl_radar_always_centered ? "currentColor" : "none"} />
                                            <path d="M3 12h3m12 0h3M12 3v3m0 12v3" />
                                            <circle cx="12" cy="12" r="9" strokeOpacity="0.3" />
                                        </svg>
                                    </Hud_ToggleButton>
                                </Tooltip>

                                <Tooltip
                                    title={t('RADAR_ROTATE_LABEL')}
                                    placement="left"
                                >
                                    <Hud_ToggleButton
                                        active={hud.cl_radar_rotate}
                                        onClick={() =>
                                            !configIsLocked && handleChange(
                                                'cl_radar_rotate',
                                                !hud.cl_radar_rotate
                                            )
                                        }
                                        disabled={configIsLocked}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21.5 2v6h-6M2.5 22v-6h6" />
                                            <path d="M2 12c0-4.4 3.6-8 8-8 1.5 0 3 .4 4.2 1.2M22 12c0 4.4-3.6 8-8 8-1.5 0-3-.4-4.2-1.2" />
                                        </svg>
                                    </Hud_ToggleButton>
                                </Tooltip>

                                <Toolbar_Separator />

                                <Tooltip
                                    title={t('SHOW_LOADOUT_LABEL')}
                                    placement="left"
                                >
                                    <Hud_ToggleButton
                                        active={hud.cl_showloadout}
                                        onClick={() =>
                                            !configIsLocked && handleChange(
                                                'cl_showloadout',
                                                !hud.cl_showloadout
                                            )
                                        }
                                        disabled={configIsLocked}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </Hud_ToggleButton>
                                </Tooltip>

                                <Tooltip
                                    title={t('TEAMMATE_COLORS_LABEL')}
                                    placement="left"
                                >
                                    <Hud_ToggleButton
                                        active={hud.cl_teammate_colors_show > 0}
                                        style={hud.cl_teammate_colors_show > 0 ? {
                                            background: 'linear-gradient(45deg, rgba(99, 102, 241, 0.2), rgba(255, 255, 255, 0.1))',
                                            borderColor: '#6366f1'
                                        } : {}}
                                        onClick={() =>
                                            !configIsLocked && handleChange(
                                                'cl_teammate_colors_show',
                                                ((hud.cl_teammate_colors_show ?? 1) + 1) % 3
                                            )
                                        }
                                        disabled={configIsLocked}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {hud.cl_teammate_colors_show === 2 ? (
                                                <>
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" opacity="0.2" />
                                                    <circle cx="8.5" cy="7" r="3" opacity="0.2" />
                                                    <text x="12" y="18" textAnchor="middle" fontSize="24" fontWeight="1000" fill="currentColor" stroke="none">A</text>
                                                </>
                                            ) : (
                                                <>
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                    <circle cx="9" cy="7" r="4" />
                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                                </>
                                            )}
                                        </svg>
                                    </Hud_ToggleButton>
                                </Tooltip>
                            </Hud_PreviewToolbar>
                        </Hud_PreviewBox>
                    </Hud_Column>

                    {/* CONTROLS COLUMN */}
                    <Hud_Column style={{ paddingTop: '10px', width: '100%', maxWidth: '400px' }}>

                        <Toggle_Row onClick={() => !configIsLocked && handleChange('cl_radar_scale_dynamic', !hud.cl_radar_scale_dynamic)}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff' }}>
                                    {t('RADAR_DYNAMIC_SCALE') || 'Dynamic Radar Scale'}
                                </span>
                                <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                                    {t('RADAR_DYNAMIC_SCALE_DESC') || 'Auto-zoom based on context (New in CS2)'}
                                </span>
                            </div>
                            <Hud_Checkbox
                                checked={hud.cl_radar_scale_dynamic}
                                disabled={configIsLocked}
                            >
                                {hud.cl_radar_scale_dynamic && (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                )}
                            </Hud_Checkbox>
                        </Toggle_Row>

                        <ConfigSlider
                            label={t('RADAR_SCALE_LABEL')}
                            description={t('RADAR_SCALE_DESC_HELP')}
                            value={hud.cl_radar_scale || 0.7}
                            min={0.25}
                            max={1.0}
                            step={0.01}
                            onChange={(v) => handleChange('cl_radar_scale', v)}
                            presets={[0.3, 0.45, 0.6, 0.7, 0.85, 1.0]}
                            disabled={configIsLocked || hud.cl_radar_scale_dynamic}
                        />

                        <ConfigSlider
                            label={t('RADAR_HUD_SIZE_LABEL')}
                            description={t('RADAR_HUD_SIZE_DESC_HELP')}
                            value={hud.cl_hud_radar_scale || 1.0}
                            min={0.8}
                            max={1.3}
                            step={0.01}
                            onChange={(v) =>
                                handleChange('cl_hud_radar_scale', v)
                            }
                            presets={[0.8, 0.9, 1.0, 1.1, 1.2, 1.3]}
                            disabled={configIsLocked}
                        />

                        <ConfigSlider
                            label={t('RADAR_ICON_SCALE_LABEL')}
                            description={t('RADAR_ICON_SCALE_DESC_HELP')}
                            value={hud.cl_radar_icon_scale_min || 0.6}
                            min={0.4}
                            max={1.0}
                            step={0.05}
                            onChange={(v) =>
                                handleChange('cl_radar_icon_scale_min', v)
                            }
                            presets={[0.4, 0.6, 0.8, 1.0]}
                            disabled={configIsLocked}
                        />

                        <ConfigSlider
                            label={t('HUD_SCALE_LABEL')}
                            description={t('HUD_SCALE_DESC_HELP')}
                            value={hud.hud_scaling || 0.9}
                            min={0.8}
                            max={1.1}
                            step={0.01}
                            onChange={(v) => handleChange('hud_scaling', v)}
                            presets={[0.8, 0.9, 1.0, 1.1]}
                            disabled={configIsLocked}
                        />

                        <Hud_ControlGroup>
                            <Hud_ColorLabelContainer>
                                <Hud_ColorLabelText>
                                    {t('HUD_COLOR_LABEL')}
                                </Hud_ColorLabelText>
                            </Hud_ColorLabelContainer>
                            <Hud_ColorGrid>
                                {colors.map((c) => (
                                    <Hud_ColorSwatch
                                        key={c.id}
                                        color={c.color}
                                        active={hud.cl_hud_color === c.id}
                                        onClick={() =>
                                            !configIsLocked && handleChange(
                                                'cl_hud_color',
                                                c.id
                                            )
                                        }
                                        disabled={configIsLocked}
                                    />
                                ))}
                            </Hud_ColorGrid>
                        </Hud_ControlGroup>

                    </Hud_Column>
                </Hud_ColumnsContainer>

                {/* PRESETS SECTION */}
                <Presets_Container>
                    <Presets_Header>
                        RADAR PRESETS
                    </Presets_Header>
                    <Presets_Grid>
                        {RADAR_PRESETS.map((preset) => (
                            <Preset_Card
                                key={preset.id}
                                onClick={() => handleApplyPreset(preset.values)}
                            >
                                <Preset_IconWrapper className="preset-icon">
                                    {preset.icon}
                                </Preset_IconWrapper>
                                <Preset_Title>{preset.title}</Preset_Title>
                                <Preset_Desc>{preset.desc}</Preset_Desc>
                            </Preset_Card>
                        ))}
                    </Presets_Grid>
                </Presets_Container>

            </Hud_Controls>
        </ModuleContainer>
    )
}
